<template>
    <div class="timepicker-container" @click="onClickContainer">
        <div
            ref="popper"
            class="popper-menu-anchor"
            :class="{'popper-menu-hide-under-navbar': !popupUsedInModal}"
        />

        <v-icon
            v-if="!disablePrefixIcon"
            :color="menuOpen? 'primary' : ''"
            class="mr-2"
            tabindex="-1"
        >
            {{ menuOpen ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}
        </v-icon>

        <v-menu
            ref="menu"
            v-model="menuOpen"
            :attach="$refs.popper"
            :close-on-content-click="false"
            content-class="menu-rounded"
            transition="popper-scale"
            :disabled="disabled"
            :min-width="pickerWidth"
            :max-width="pickerWidth"
        >
            <template #activator="{ on, attrs }">
                <v-text-field
                    ref="textField"
                    v-model="textFieldValue"
                    :label="label"
                    :disabled="disabled"
                    :placeholder="placeholder"
                    :clearable="clearable && !disabled"
                    :aria-label="label ? label : 'date/time picker'"
                    dense
                    outlined
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                    :suffix="suffix"

                    @keyup.enter="handleEnterKeyPress"
                    @keydown.tab="handleActivatorTab"
                    @blur="handleBlur"
                    @click:clear="handleClear"
                />
            </template>
            <TimepickerPanel
                ref="panel"
                :hours="hours"
                :minutes="minutes"
                :ampm="ampm"
                :format="format"
                :clearable="clearable"
                :hasValue="!!innerValue"

                @update:hours="hours = $event"
                @update:minutes="minutes = $event"
                @update:ampm="ampm = $event"
                @apply="handleApply"
                @clear="handleClear"
                @tabNext="handlePanelTabNext"
                @tabPrev="handlePanelTabPrev"
            />
        </v-menu>
    </div>
</template>

<script>
import popperPositionedVuetifyMenuMixin from '../../mixins/popperPositionedVuetifyMenuMixin';
import {Portal} from '@linusborg/vue-simple-portal';
import {getTimeFormat} from "../../../utils/time";
import {
    parseRawInput,
    clampTime,
    toDisplay,
    applyAMPM,
    clampBetween, focusNextFocusable, focusPrevFocusable
} from "../../../utils/timepicker";
import TimepickerPanel from "./timepicker-panel.vue";

export default {
    name: 'Timepicker',
    components: {TimepickerPanel, Portal},
    mixins: [popperPositionedVuetifyMenuMixin],
    props: {
        value: String,
        label: String,
        disabled: Boolean,
        min: String,
        max: String,
        placeholder: String,
        popupUsedInModal: Boolean,
        clearable: {type: Boolean, default: true},
        timepickerMode: {type: Number, default: Laravel.timepickerMode},
        disablePrefixIcon: Boolean
    },
    data() {
        return {
            menuOpen: false,
            hours: '',
            minutes: '',
            ampm: 'am',
            rawInput: '',
            currentTimepickerMode: undefined,
            ignoreNextBlur: false,
        };
    },
    computed: {
        format() {
            return getTimeFormat(this.currentTimepickerMode);
        },
        pickerWidth() {
            return this.format === 'ampm' ? '190px' : '170px';
        },
        innerValue: {
            get() {
                return this.value || '';
            },
            set(val) {
                if (val !== this.value) {
                    this.$emit('input', val);
                    this.$emit('change', val);
                }
            }
        },
        suffix() {
            if (this.currentTimepickerMode !== 1) return undefined;
            return this.ampm;
        },
        textFieldValue: {
            get() {
                if (this.menuOpen) return this.rawInput;

                if (!this.innerValue) return '';

                if (this.format === 'ampm') return this.formatDisplay(this.innerValue);

                return this.innerValue;
            },
            set(val) {
                this.rawInput = val;
            }
        }
    },
    watch: {
        value() {
            this.syncPartsFromValue();
        },
        menuOpen(val) {
            if (val) {
                this.syncPartsFromValue();

                this.rawInput = this.format === 'ampm'
                    ? this.formatDisplay(this.innerValue)
                    : this.innerValue;
            }
        }
    },
    mounted() {
        this.currentTimepickerMode = this.timepickerMode;
        this.syncPartsFromValue();

        EaseEventBus.listen('timepickerModeUpdated', (mode) => {
            this.currentTimepickerMode = mode;
        });
    },
    methods: {
        getReferenceEl() {
            return this.$refs.textField && this.$refs.textField.$el;
        },
        getPopperEl() {
            return this.$refs.popper;
        },
        getVMenuEl() {
            return this.$refs.menu && this.$refs.menu.$el;
        },
        syncPartsFromValue() {
            if (!this.innerValue) {
                this.hours = '';
                this.minutes = '';
                this.ampm = 'am';
                return;
            }

            const [hStr, mStr] = this.innerValue.split(':');
            let h = Number(hStr);

            if (this.format === 'ampm') {
                this.ampm = h >= 12 ? 'pm' : 'am';
                h = h % 12;
                if (h === 0) h = 12;

                this.hours = String(h);
                this.minutes = mStr || '00';
            } else {
                this.hours = hStr;
                this.minutes = mStr || '00';
            }
        },
        formatDisplay(time24) {
            return this.format === 'ampm' ? toDisplay(time24) : time24;
        },
        handlePanelTabNext(from) {
            const p = this.$refs.panel;
            if (!p) return;

            const clearBtn = p.$el.querySelector('.v-btn:not([color])');       // Clear
            const applyBtn = p.$el.querySelector('.primary');                  // Apply
            const ampmBtn  = p.$el.querySelector('.v-btn-toggle .v-btn');      // AM/PM

            if (from === 'hours') {
                p.getMinutesInput()?.focus();
                return;
            }

            if (from === 'minutes') {
                if (this.format === 'ampm') {
                    ampmBtn?.focus();
                    return;
                }

                if (clearBtn) {
                    clearBtn.focus();
                    return;
                }

                applyBtn?.focus();
                return;
            }

            if (from === 'ampm') {
                if (clearBtn) {
                    clearBtn.focus();
                    return;
                }
                applyBtn?.focus();
                return;
            }

            if (from === 'clear') {
                applyBtn?.focus();
                return;
            }

            if (from === 'apply') {
                this.exitMenu(true);
            }
        },
        handlePanelTabPrev(from) {
            const p = this.$refs.panel;
            if (!p) return;

            const clearBtn = p.$el.querySelector('.v-btn:not([color])');
            const ampmBtn  = p.$el.querySelector('.v-btn-toggle .v-btn');

            if (from === 'hours') {
                this.exitMenu(false); // EXIT BACKWARDS
                return;
            }

            if (from === 'minutes') {
                p.getHoursInput()?.focus();
                return;
            }

            if (from === 'ampm') {
                p.getMinutesInput()?.focus();
                return;
            }

            if (from === 'clear') {
                if (this.format === 'ampm') {
                    ampmBtn?.focus();
                    return;
                }
                p.getMinutesInput()?.focus();
                return;
            }

            if (from === 'apply') {
                if (clearBtn) {
                    clearBtn.focus();
                    return;
                }

                if (this.format === 'ampm') {
                    ampmBtn?.focus();
                    return;
                }

                p.getMinutesInput()?.focus();
            }
        },
        exitMenu(forward = true) {
            this.menuOpen = false;

            this.$nextTick(() => {
                const activatorInput = this.$refs.textField?.$el?.querySelector('input');
                if (!activatorInput) return;

                if (forward) {
                    focusNextFocusable(activatorInput);
                } else {
                    focusPrevFocusable(activatorInput);
                }
            });
        },
        handleEnterKeyPress() {
            this.applyChanges();
        },
        applyChanges() {
            if (!this.rawInput || !this.rawInput.trim()) {
                this.handleClear();
                return;
            }

            this.ignoreNextBlur = true;

            this.parseRawInput();

            if (this.menuOpen) {
                this.menuOpen = false;
            }

            this.$nextTick(() => {
                const inputEl = this.$refs.textField?.$el?.querySelector('input');
                if (inputEl) inputEl.blur();
            });
        },
        parseRawInput() {
            const parsed = parseRawInput(this.rawInput, this.format);
            if (!parsed) {
                this.innerValue = '';
                return;
            }
            this.innerValue = clampBetween(parsed, this.min, this.max);
        },
        handleApply() {
            this.ignoreNextBlur = true;

            let h = Number(this.hours || 0);
            let m = Number(this.minutes || 0);

            if (this.format === 'ampm') {
                h = applyAMPM(h, this.ampm);
            }

            const time = clampTime(h, m);
            this.innerValue = clampBetween(time, this.min, this.max);

            this.rawInput = time;

            this.menuOpen = false;
            this.$refs.textField.focus();
        },
        handleClear() {
            this.hours = '';
            this.minutes = '';
            this.innerValue = '';
            this.rawInput = '';
            this.menuOpen = false;
        },
        handleBlur() {
            if (this.ignoreNextBlur) {
                this.ignoreNextBlur = false;
                return;
            }

            if (this.menuOpen) return;

            if (!this.rawInput || !this.rawInput.trim()) {
                this.handleClear();
                return;
            }

            this.applyChanges();
        },
        onClickContainer(e) {
            if (this.menuShown) return;

            this.menuOpen = !this.menuOpen;
        },
        handleActivatorTab(e) {
            if (e.shiftKey) {
                this.menuOpen = false;
                return;
            }

            e.preventDefault();

            if (!this.menuOpen) {
                this.menuOpen = true;
            }

            this.$nextTick(() => {
                const panel = this.$refs.panel;
                if (!panel) return;

                const hoursInput = panel.getHoursInput();
                if (hoursInput) hoursInput.focus();
            });
        },
    }
};
</script>

<style scoped>
.timepicker-container {
    display: flex;
    cursor: text !important;
}
</style>
